import React, { useCallback, useEffect, useState } from "react";
import { UploadCloud, Link2, Cloud, RefreshCw, Trash2, ShieldCheck, CheckCircle2, AlertTriangle, Clock, Undo2, Rocket } from "lucide-react";
import { apiGet, apiPut, apiPost, apiDelete, apiUploadFile } from "../../utils/api";
import { toast } from "../../utils/toast";
import "./agent-release-settings.css";

const APP_OPTIONS = [
  { value: "agent-user", label: "Agent (User)" },
  { value: "agent-admin", label: "Agent (Admin)" },
];

const MODES = [
  { value: "s3", label: "S3 Bucket", icon: Cloud, desc: "Default. The signed Contabo release bucket managed by build_release.py." },
  { value: "url", label: "URL Link", icon: Link2, desc: "Backend fetches the zip from a URL you provide and streams it through — agents never see the URL directly." },
  { value: "upload", label: "Manual Upload", icon: UploadCloud, desc: "Serve a zip + version.json uploaded here, straight from this server's disk. Use when S3 is unreachable." },
];

function backupLabel(status) {
  if (status?.local?.ready) return "Manual Upload";
  if (status?.externalUrl && status?.externalVersion) return "URL Link";
  return null;
}

export default function AgentReleaseSettings() {
  const [appId, setAppId] = useState("agent-user");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMode, setSavingMode] = useState(false);

  const [pendingMode, setPendingMode] = useState("s3");
  const [urlForm, setUrlForm] = useState({ externalUrl: "", externalVersion: "" });
  const [autoFailover, setAutoFailover] = useState(false);

  const [zipFile, setZipFile] = useState(null);
  const [zipUploading, setZipUploading] = useState(false);
  const [manifestFile, setManifestFile] = useState(null);
  const [manifestUploading, setManifestUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [forcingS3, setForcingS3] = useState(false);
  const [promoteVersion, setPromoteVersion] = useState("");
  const [promoting, setPromoting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet(`/api/admin/agent-releases/${appId}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus(data);
        setPendingMode(data.mode || "s3");
        setUrlForm({ externalUrl: data.externalUrl || "", externalVersion: data.externalVersion || "" });
        setAutoFailover(!!data.autoFailoverEnabled);
      } else {
        toast.error(data.message || "Failed to load release settings");
      }
    } catch {
      toast.error("Network error loading release settings");
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => { void load(); }, [load]);

  async function handleSaveMode() {
    if (pendingMode === "upload" && !status?.local?.ready) {
      toast.error("Upload both a zip and a version.json before switching to Manual Upload mode.");
      return;
    }
    if (pendingMode === "url" && (!urlForm.externalUrl.trim() || !urlForm.externalVersion.trim())) {
      toast.error("URL Link mode needs both a URL and a version.");
      return;
    }
    setSavingMode(true);
    try {
      const res = await apiPut(`/api/admin/agent-releases/${appId}/mode`, {
        mode: pendingMode,
        externalUrl: urlForm.externalUrl,
        externalVersion: urlForm.externalVersion,
        autoFailoverEnabled: autoFailover,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${appId} now serving from: ${pendingMode}`);
        await load();
      } else {
        toast.error(data.message || "Failed to update mode");
      }
    } catch {
      toast.error("Network error updating mode");
    } finally {
      setSavingMode(false);
    }
  }

  async function handleUploadZip() {
    if (!zipFile) return;
    setZipUploading(true);
    try {
      const res = await apiUploadFile(`/api/admin/agent-releases/${appId}/zip`, zipFile);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Zip uploaded (${(data.size / 1024 / 1024).toFixed(1)} MB)`);
        setZipFile(null);
        await load();
      } else {
        toast.error(data.message || "Zip upload failed");
      }
    } catch {
      toast.error("Network error uploading zip");
    } finally {
      setZipUploading(false);
    }
  }

  async function handleUploadManifest() {
    if (!manifestFile) return;
    setManifestUploading(true);
    try {
      const text = await manifestFile.text();
      const parsed = JSON.parse(text);
      const res = await apiPost(`/api/admin/agent-releases/${appId}/manifest`, parsed);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`version.json saved (version ${data.manifest?.version})`);
        setManifestFile(null);
        await load();
      } else {
        toast.error(data.message || "Manifest upload failed");
      }
    } catch (e) {
      toast.error(e.message?.includes("JSON") ? "That file isn't valid JSON" : "Manifest upload failed");
    } finally {
      setManifestUploading(false);
    }
  }

  async function handleForceS3() {
    if (!window.confirm(`Force ${appId} back to S3 mode, clearing URL Link and disabling auto-failover?`)) return;
    setForcingS3(true);
    try {
      const res = await apiPut(`/api/admin/agent-releases/${appId}/force-s3`, {});
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${appId} forced back to S3 mode`);
        await load();
      } else {
        toast.error(data.message || "Failed to force S3 mode");
      }
    } catch {
      toast.error("Network error forcing S3 mode");
    } finally {
      setForcingS3(false);
    }
  }

  async function handlePromote() {
    if (!/^\d+\.\d+\.\d+$/.test(promoteVersion.trim())) {
      toast.error("Enter a version in x.y.z format (must already be archived by a build).");
      return;
    }
    if (!window.confirm(`Promote ${appId} ${promoteVersion.trim()} to '${status?.serverChannel}'? This is what this backend's agents will fetch immediately.`)) return;
    setPromoting(true);
    try {
      const res = await apiPost(`/api/admin/agent-releases/${appId}/promote`, { version: promoteVersion.trim() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${appId} '${status?.serverChannel}' is now ${data.version}`);
        setPromoteVersion("");
        await load();
      } else {
        toast.error(data.message || "Promotion failed");
      }
    } catch {
      toast.error("Network error promoting version");
    } finally {
      setPromoting(false);
    }
  }

  async function handleClearLocal() {
    if (!window.confirm("Clear the uploaded zip + version.json for this app?")) return;
    setClearing(true);
    try {
      const res = await apiDelete(`/api/admin/agent-releases/${appId}/local`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Local override files cleared");
        await load();
      } else {
        toast.error(data.message || "Failed to clear");
      }
    } catch {
      toast.error("Network error clearing local files");
    } finally {
      setClearing(false);
    }
  }

  const failoverEngaged = !status?.s3?.health?.healthy && status?.autoFailoverEnabled && status?.mode === "s3";

  return (
    <div className="admin-panel" style={{ marginTop: "20px" }}>
      <div className="admin-panel-head">
        <div>
          <h2 className="admin-panel-title">Agent Release Source</h2>
          <p className="admin-panel-note">
            Controls where <code>/api/agent/update</code> and its download endpoint serve builds from —
            affects every tenant's agents on this backend. Switch away from S3 only during an outage.
          </p>
        </div>
        <div className="admin-actions">
          {status?.mode !== "s3" && (
            <button className="admin-btn-danger" onClick={handleForceS3} disabled={forcingS3}>
              <Undo2 size={14} /> {forcingS3 ? "Forcing…" : "Force S3 Now"}
            </button>
          )}
          <button className="gc-icon-btn" data-tip="Refresh" onClick={load} disabled={loading}>
          <RefreshCw size={14} />
        </button>
        </div>
      </div>

      <div className="admin-form-field ars-app-picker">
        <label>App</label>
        <select className="admin-input" value={appId} onChange={e => setAppId(e.target.value)}>
          {APP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="admin-empty-state" style={{ marginTop: "16px" }}>Loading…</div>
      ) : (
        <>
          <div className="ars-mode-grid">
            {MODES.map(m => {
              const Icon = m.icon;
              const selected = pendingMode === m.value;
              const isLive = status?.mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setPendingMode(m.value)}
                  className={`ars-mode-card${selected ? " is-selected" : ""}`}
                >
                  <div className="ars-mode-card-head">
                    <div className="ars-mode-card-icon"><Icon size={16} /></div>
                    {isLive && <span className="admin-status is-active">Live</span>}
                  </div>
                  <div className="ars-mode-card-title">{m.label}</div>
                  <div className="ars-mode-card-desc">{m.desc}</div>
                </button>
              );
            })}
          </div>

          {pendingMode === "s3" && (
            <div className="ars-section" style={{ border: "1px solid var(--as-border, #edebe9)", borderRadius: "var(--as-r, 4px)", padding: "14px" }}>
              <div className="ars-failover-title" style={{ marginBottom: "4px" }}>
                <Rocket size={15} /> Promote a version to <code>{status?.serverChannel || "…"}</code>
              </div>
              <div className="ars-failover-desc" style={{ marginBottom: "10px" }}>
                Re-points this server's channel at an already-archived version (server-side bucket copy — no
                rebuild). This server can only promote to its own channel (<code>{status?.serverChannel}</code>),
                set via <code>RELEASE_S3_CHANNEL</code> — never the other one.
              </div>
              <div className="ars-file-row">
                <input
                  className="admin-input"
                  style={{ maxWidth: "160px" }}
                  type="text"
                  placeholder="1.8.2"
                  value={promoteVersion}
                  onChange={e => setPromoteVersion(e.target.value)}
                />
                <button className="admin-btn" onClick={handlePromote} disabled={promoting || !promoteVersion.trim()}>
                  {promoting ? "Promoting…" : `Promote to ${status?.serverChannel || "…"}`}
                </button>
              </div>
            </div>
          )}

          {pendingMode === "s3" && (
            <div className="ars-failover-box">
              <label className="ars-switch">
                <input
                  type="checkbox"
                  checked={autoFailover}
                  onChange={e => setAutoFailover(e.target.checked)}
                />
                <span className="ars-switch-track" />
              </label>
              <div className="ars-failover-copy">
                <div className="ars-failover-title"><ShieldCheck size={15} /> Auto-failover</div>
                <div className="ars-failover-desc">
                  If S3 becomes unreachable (checked every ~2 min in the background), agents are automatically
                  served from Manual Upload if one is staged, otherwise from URL Link if one is configured — no
                  need to flip this toggle by hand during an outage. Switches back the moment S3 recovers.
                  Requires a backup staged in advance.
                </div>
              </div>
            </div>
          )}

          {pendingMode === "url" && (
            <div className="ars-section admin-form-grid">
              <div className="admin-form-field">
                <label>Update URL (zip download link)</label>
                <input
                  className="admin-input"
                  type="text"
                  placeholder="https://example.com/agent-user.zip"
                  value={urlForm.externalUrl}
                  onChange={e => setUrlForm(f => ({ ...f, externalUrl: e.target.value }))}
                />
              </div>
              <div className="admin-form-field">
                <label>Version</label>
                <input
                  className="admin-input"
                  type="text"
                  placeholder="1.8.1"
                  value={urlForm.externalVersion}
                  onChange={e => setUrlForm(f => ({ ...f, externalVersion: e.target.value }))}
                />
              </div>
            </div>
          )}

          {pendingMode === "upload" && (
            <div className="ars-section">
              <div className="ars-upload-grid">
                <div className={`ars-upload-card${status?.local?.hasZip ? " is-ready" : ""}`}>
                  <div className="ars-upload-card-title">
                    {status?.local?.hasZip ? <CheckCircle2 size={14} /> : <UploadCloud size={14} />}
                    Agent zip {status?.local?.hasZip && "— uploaded"}
                  </div>
                  <div className="ars-file-row">
                    <input className="ars-file-input" type="file" accept=".zip" onChange={e => setZipFile(e.target.files?.[0] || null)} />
                    <button className="admin-inline-action" onClick={handleUploadZip} disabled={!zipFile || zipUploading}>
                      {zipUploading ? "Uploading…" : "Upload"}
                    </button>
                  </div>
                </div>

                <div className={`ars-upload-card${status?.local?.manifest ? " is-ready" : ""}`}>
                  <div className="ars-upload-card-title">
                    {status?.local?.manifest ? <CheckCircle2 size={14} /> : <UploadCloud size={14} />}
                    version.json {status?.local?.manifest && `— v${status.local.manifest.version}`}
                  </div>
                  <div className="ars-file-row">
                    <input className="ars-file-input" type="file" accept=".json" onChange={e => setManifestFile(e.target.files?.[0] || null)} />
                    <button className="admin-inline-action" onClick={handleUploadManifest} disabled={!manifestFile || manifestUploading}>
                      {manifestUploading ? "Uploading…" : "Upload"}
                    </button>
                  </div>
                </div>
              </div>

              {(status?.local?.hasZip || status?.local?.manifest) && (
                <div className="admin-actions" style={{ marginTop: "10px" }}>
                  <button className="admin-btn-danger" onClick={handleClearLocal} disabled={clearing}>
                    <Trash2 size={14} /> {clearing ? "Clearing…" : "Clear uploaded files"}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="admin-actions" style={{ marginTop: "18px" }}>
            <button className="admin-btn" onClick={handleSaveMode} disabled={savingMode}>
              {savingMode ? "Saving…" : `Activate "${MODES.find(m => m.value === pendingMode)?.label}"`}
            </button>
          </div>

          {pendingMode === "s3" && status?.s3 && (status.s3.configured === false || !status.s3.manifestFound) && (
            <div className="ars-alert is-danger">
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: "1px" }} />
              <span>
                {status.s3.configured === false ? (
                  <>
                    <strong>S3 isn't configured on this backend</strong> — RELEASE_S3_ENDPOINT / BUCKET /
                    ACCESS_KEY_ID / SECRET_ACCESS_KEY must all be set in <code>backend/src/.env</code>.
                  </>
                ) : !status.s3.liveCheck?.healthy ? (
                  <>
                    <strong>S3 endpoint unreachable</strong> — {status.s3.liveCheck?.reason || "unknown error"}.
                    Check the endpoint URL and network access from this server.
                  </>
                ) : (
                  <>
                    <strong>S3 is reachable but no manifest was found</strong> for {appId} on this backend's
                    channel — nothing has been promoted here yet via <code>build_release.py --deploy</code>.
                  </>
                )}
              </span>
            </div>
          )}

          {failoverEngaged && (
            <div className="ars-alert is-danger">
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: "1px" }} />
              <span>
                <strong>Failover engaged</strong> — S3 is unreachable, agents are currently being served from{" "}
                <strong>{backupLabel(status) || "no backup (still failing)"}</strong> instead.
              </span>
            </div>
          )}

          <div className="ars-status-footer admin-inline-metrics">
            <div className="admin-metric-card">
              <span className="admin-metric-label">Configured Mode</span>
              <span className="admin-metric-value" style={{ fontSize: "0.95rem" }}>{status?.mode || "s3"}</span>
            </div>
            <div className="admin-metric-card">
              <span className="admin-metric-label">S3 Health</span>
              <span className={`admin-status ${status?.s3?.health?.healthy ? "is-active" : "is-danger"}`} style={{ marginTop: "4px" }}>
                {status?.s3?.health?.healthy ? "Healthy" : "Unhealthy"}
              </span>
            </div>
            {status?.s3?.health?.lastCheckedAt && (
              <div className="admin-metric-card">
                <span className="admin-metric-label">Last Checked</span>
                <span className="admin-metric-value" style={{ fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "5px" }}>
                  <Clock size={13} /> {new Date(status.s3.health.lastCheckedAt).toLocaleTimeString()}
                </span>
              </div>
            )}
            {status?.s3?.manifest?.version && (
              <div className="admin-metric-card">
                <span className="admin-metric-label">S3 Version</span>
                <span className="admin-metric-value" style={{ fontSize: "0.95rem" }}>{status.s3.manifest.version}</span>
              </div>
            )}
            {status?.mode === "url" && status?.externalVersion && (
              <div className="admin-metric-card">
                <span className="admin-metric-label">URL Version</span>
                <span className="admin-metric-value" style={{ fontSize: "0.95rem" }}>{status.externalVersion}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
